import { LogIn, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

interface UserProfilePanelProps {
  user: any;
  onSignOut: () => void;
  onSignIn: () => void;
  isSigningOut?: boolean;
}

export function UserProfilePanel({
  user,
  onSignOut,
  onSignIn,
  isSigningOut = false
}: UserProfilePanelProps) {
  const getUserInitials = () => {
    if (user?.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return "A";
  };

  const getUserDisplayName = () => {
    if (isSigningOut) {
      return "signing out";
    }
    if (user?.email) {
      return user.email.split('@')[0];
    }
    return "Anonymous";
  };

  const isAnonymous = user?.is_anonymous || !user?.email;

  return (
    <div className="flex flex-col h-full p-4">
      <div className="mb-4">
        <h2 className="text-lg font-semibold">Profile</h2>
        <p className="text-sm text-muted-foreground">Your account information</p>
      </div>

      {/* Spacer to push card to bottom */}
      <div className="flex-1" />

      {/* Profile Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col items-center gap-4">
            <Avatar className="h-20 w-20">
              <AvatarFallback className="bg-primary/10 text-primary text-2xl">
                {getUserInitials()}
              </AvatarFallback>
            </Avatar>
            
            <div className="text-center">
              <p className="text-lg font-medium">{getUserDisplayName()}</p>
              <p className="text-sm text-muted-foreground">
                {isAnonymous ? 'Guest User' : 'Registered User'}
              </p>
            </div>

            <Separator className="my-2" />

            {/* Account Details */}
            <div className="w-full space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Status</span>
                <span className="font-medium">{isAnonymous ? 'Guest' : 'Signed In'}</span>
              </div>
              {!isAnonymous && user?.email && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Email</span>
                  <span className="font-medium truncate max-w-[200px]">{user.email}</span>
                </div>
              )}
            </div>

            <Separator className="my-2" />

            {/* Sign In/Out Button */}
            {user ? (
              <Button
                variant="outline"
                className="w-full"
                onClick={onSignOut}
                disabled={isSigningOut}
              >
                <LogOut className="h-4 w-4 mr-2" />
                {isSigningOut ? 'Signing out...' : 'Sign Out'}
              </Button>
            ) : (
              <Button
                variant="default"
                className="w-full"
                onClick={onSignIn}
              >
                <LogIn className="h-4 w-4 mr-2" />
                Sign In
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Export compact version for use elsewhere
export { UserProfileCompact } from "./UserProfileCompact";

